import React, { Fragment, useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableCheckboxCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderCheckboxCell,
    DataTableHeaderRow,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    Search,
    SortDirection,
    Checkbox
} from "@episerver/ui-framework";
import {
    addContactsTitleSelector,
    contactListPaginationSelector,
    deselectAllContacts,
    deselectContact,
    isOpenDialogSelector,
    keywordSelector,
    onChangeDialog,
    selectAllContacts,
    selectContact,
    selectedContactsSelector,
    sort,
    sortSelector,
    changeKeyword,
    contactsSelector,
} from "../reducers/addAssignmentsToUserReducer";
import {userAccountAssignmentsSelector} from "../reducers/userAccountAssignmentsReducer";
import { Contact, ContactType, ServerSortType } from "../../ContactManager/model/contact";
import { searchAvailableUserAccountAssignments, updateUserAccountAssignments} from "../../ContactManager/reducers/asyncThunks";
import { debounce } from "lodash-es";

interface AddAssignmentsToUsernProps {
    contactId: string;
    type: ContactType;
}

const AddAssignmentsToUser = (props: AddAssignmentsToUsernProps) => {
    const dispatch = useAppDispatch();
    const contacts = useAppSelector(contactsSelector);
    const assignedContacts = useAppSelector(userAccountAssignmentsSelector);
    const { sortDirection, sortedColumn } = useAppSelector(sortSelector);
    const sortDirectionRef = useRef<SortDirection>(sortDirection);
    const sortedColumnRef = useRef<string>(sortedColumn);
    const { contactId } = props;
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useAppSelector(contactListPaginationSelector);
    const keyword = useAppSelector(keywordSelector);
    const selectedContacts = useAppSelector(selectedContactsSelector);
    const open = useAppSelector(isOpenDialogSelector);
    const title = useAppSelector(addContactsTitleSelector);

    useEffect(() => {  
        dispatch(
            searchAvailableUserAccountAssignments({
                query: keyword,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
                contactId: contactId
            })
        );
       
    }, []);

    const columns: Array<{ selector: string; title: string; sortable: boolean }> = props.type !== ContactType.Account ?  [
        { selector: "FullName", title: "Full Name", sortable: true },
        { selector: "ContactCode", title: "ContactCode", sortable: true },
    ] : [
        { selector: "FullName", title: "Full Name", sortable: true },
        { selector: "Email", title: "Email", sortable: true },
    ];

    const onSelectedRowChanged = (row: Contact, checked: boolean): void => {
        if (checked) dispatch(selectContact(row));
        else dispatch(deselectContact(row));
    };

    const onToggleAllSelected = (checked: boolean): void => {
        if (checked) dispatch(selectAllContacts());
        else dispatch(deselectAllContacts());
    };

    const onSort = (column: string, direction: SortDirection): void => {
        dispatch(sort({ column, direction }));
            dispatch(
                searchAvailableUserAccountAssignments({
                    query: keyword,
                    sortField: column,
                    sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    contactId: contactId
                })
            );
       
    };

    useEffect(() => {
        sortDirectionRef.current = sortDirection;
    }, [sortDirection]);

    useEffect(() => {
        sortedColumnRef.current = sortedColumn;
    }, [sortedColumn]);

    const isSelected = (row: Contact): boolean => {
        return selectedContacts.find((x) => x.primaryKeyId == row.primaryKeyId) != null;
    };

    const handleSearch = (value: string, recordsToRetrieve: number) => debouncedSearch(value, recordsToRetrieve);
    const debouncedSearch = useCallback(
        debounce((nextValue: string, recordsToRetrieve: number) => onSearch(nextValue, recordsToRetrieve), 1000),
        []
    );
    const onSearch = (value: string, recordsToRetrieve: number): void => {
            dispatch(
                searchAvailableUserAccountAssignments({
                    query: value,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: recordsToRetrieve,
                    contactId: contactId
                })
            );
        dispatch(changeKeyword(value));
    };

    const onChangePage = (startRow: number): void => {
            dispatch(
                searchAvailableUserAccountAssignments({
                    query: keyword,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    contactId: contactId
                })
            );
    };

    const onChangeRowsPerPage = (rowsPerPage: number): void => {
            dispatch(
                searchAvailableUserAccountAssignments({
                    query: keyword,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    contactId: contactId
                })
            );
    };

    const onSubmit = async (): Promise<void> => {
        dispatch(updateUserAccountAssignments({contactIds: selectedContacts.map(contact => contact.primaryKeyId), contactId: contactId}));
        dispatch(changeKeyword(""));
        dispatch(
            searchAvailableUserAccountAssignments({
                query: keyword,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
                contactId: contactId
            })
        );
        dispatch(onChangeDialog({ open: false }));
    };

    const renderRow = (rowData: Contact): React.ReactElement => {
        var isSelectedProp = isSelected(rowData);
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId} selected={isSelectedProp}>
                <DataTableCell>
                    <Checkbox 
                        checked={isSelectedProp} 
                        onChange={(e) => onSelectedRowChanged(rowData, e.target.checked)}/>
                </DataTableCell>
                <DataTableCell>{rowData.fullName}</DataTableCell>
                {props.type !== ContactType.Account && <DataTableCell>{rowData.extendedProperties?.find(property => property.name ==="ContactCode")?.value ?? ""}</DataTableCell>}
                {props.type === ContactType.Account && <DataTableCell>{rowData.email ?? ""}</DataTableCell>}
            </DataTableRow>
        );
    };

    return (
        <Dialog
            open={open}
            title={title}
            dismissLabel="Cancel"
            confirmLabel="Update"
            enableConfirm={true}
            onInteraction={(evt) => {
                if (evt.detail.action === "confirm") {
                    onSubmit();
                } else {
                    dispatch(onChangeDialog({ open: false }));

                    dispatch(
                        searchAvailableUserAccountAssignments({
                            query: keyword,
                            sortField: sortedColumnRef.current,
                            sortType:
                                sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                            startIndex: startRow,
                            recordsToRetrieve: rowsPerPage,
                            contactId: contactId
                        })
                    );
                }
            }}
        >
            <div className="organization-list">
                <DataTable>
                    <DataTableToolbar className="organization-list__toolbar">
                        <Search
                            onValueChange={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            onSearch={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            value={keyword}
                        />
                    </DataTableToolbar>
                    <DataTableContent>
                        <DataTableHeaderRow>
                            <Fragment>
                                {contacts.length > 0 && (
                                    <DataTableHeaderCheckboxCell
                                        onChange={(e) => onToggleAllSelected(e.target.checked)}
                                    />
                                )}
                            </Fragment>
                            <React.Fragment>
                                {columns.map((column) => {
                                    if (column.sortable)
                                        return (
                                            <DataTableColumnHeaderCell
                                                key={column.selector}
                                                onSort={(_header: string, direction: SortDirection) =>
                                                    onSort(column.selector, direction)
                                                }
                                                sortDirection={
                                                    sortedColumn === column.selector
                                                        ? sortDirection
                                                        : SortDirection.Unsorted
                                                }
                                            >
                                                {column.title}
                                            </DataTableColumnHeaderCell>
                                        );
                                    return (
                                        <DataTableColumnHeaderCell key={column.selector}>
                                            {column.title}
                                        </DataTableColumnHeaderCell>
                                    );
                                })}
                            </React.Fragment>
                        </DataTableHeaderRow>
                        <DataTableBody>
                            {contacts.map((row: Contact) => {
                                return renderRow(row);
                            })}
                        </DataTableBody>
                    </DataTableContent>
                    <DataTablePagination
                        startRow={startRow}
                        totalRowCount={totalRowCount}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onChangePage={(startRow) => onChangePage(startRow)}
                        onChangeRowsPerPage={(rowsPerPage) => onChangeRowsPerPage(rowsPerPage)}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default AddAssignmentsToUser;
